import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '@/config/api';
import { useAuth } from '@/context/auth-context';

interface Site {
  id: string;
  _id?: string; // Keep for backward compatibility
  name: string;
  url: string;
  domain?: string;
  status: string;
  type?: string;
  sector_id?: number; // Q&A System sector assignment
  sector_ids?: number[]; // Multiple sector support
  offering_type?: 'product' | 'service' | 'mixed';
}

interface SiteContextType {
  selectedSite: Site | null;
  setSelectedSite: (site: Site | null) => void;
  sites: Site[];
  setSites: (sites: Site[]) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export function SiteProvider({ children }: { children: ReactNode }) {
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load selected site from backend/localStorage on mount
  useEffect(() => {
    if (!user) return;
    
    const loadUserPreferences = async () => {
      try {
        // Try to load from backend first
        const response = await axios.get(`${API_URL}/users/me/preferences`);
        if (response.data.selectedSiteId) {
          // Find the site in the sites array once loaded
          const site = sites.find(s => (s.id || s._id) === response.data.selectedSiteId);
          if (site) {
            setSelectedSite(site);
            return;
          }
        }
      } catch (error) {
        console.log('Could not load user preferences, falling back to localStorage');
      }
      
      // Fallback to localStorage
      const savedSiteId = localStorage.getItem('selectedSiteId');
      const savedSite = localStorage.getItem('selectedSite');
      
      if (savedSite) {
        try {
          const site = JSON.parse(savedSite);
          setSelectedSite(site);
        } catch (e) {
          console.error('Error parsing saved site:', e);
        }
      }
    };
    
    loadUserPreferences();
  }, [user, sites]);

  // Save selected site to backend and localStorage when it changes
  useEffect(() => {
    if (!user) return;
    
    if (selectedSite) {
      // Save to localStorage immediately
      localStorage.setItem('selectedSiteId', selectedSite.id || selectedSite._id || '');
      localStorage.setItem('selectedSite', JSON.stringify(selectedSite));
      
      // Save to backend (async, don't block)
      axios.put(`${API_URL}/users/me/preferences`, {
        selectedSiteId: selectedSite.id || selectedSite._id
      }).catch(error => {
        console.error('Failed to save site preference to backend:', error);
      });
    } else {
      localStorage.removeItem('selectedSiteId');
      localStorage.removeItem('selectedSite');
    }
  }, [selectedSite, user]);

  // When sites are loaded, select the first valid one if none is selected
  useEffect(() => {
    if (sites.length > 0 && !selectedSite) {
      const activeSites = sites.filter(site => site && site.status === 'active');
      if (activeSites.length > 0) {
        setSelectedSite(activeSites[0]);
      }
    }
  }, [sites, selectedSite]);

  // Sync selectedSite with fresh data from sites array when loaded
  // This handles cases where localStorage has stale data (e.g., missing sector_ids)
  useEffect(() => {
    if (!selectedSite || sites.length === 0) return;

    const siteId = selectedSite.id || selectedSite._id;
    const freshSite = sites.find(s => (s.id || s._id) === siteId);

    if (freshSite) {
      // Check if we need to update (e.g., sector_ids or offering_type missing/stale in selectedSite)
      const needsUpdate =
        (!selectedSite.sector_ids && freshSite.sector_ids) ||
        (selectedSite.sector_ids?.length !== freshSite.sector_ids?.length) ||
        (selectedSite.offering_type !== freshSite.offering_type);

      if (needsUpdate) {
        console.log('[SiteContext] Syncing selectedSite with fresh data from API');
        setSelectedSite(freshSite);
      }
    }
  }, [sites]);

  return (
    <SiteContext.Provider value={{
      selectedSite,
      setSelectedSite,
      sites,
      setSites,
      loading,
      setLoading,
    }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
}